import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { GiHamburgerMenu } from "react-icons/gi";
import { HiX } from "react-icons/hi";
import { getNavigation, getNavigationCache } from "../../services";

function PhoneDrawer({ onClose, isOpen, router, navigation }) {
    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop (Zero Blur) */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: "linear" }}
                        onClick={onClose}
                        className="fixed inset-0 z-[150] bg-black/85"
                    />

                    {/* Drawer Content (Zero Blur, Linear Motion) */}
                    <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ duration: 0.4, ease: "linear" }}
                        className="fixed top-0 right-0 bottom-0 z-[160] w-[280px] max-w-[85vw] bg-[#0e1018] border-l border-pink/20 p-6 flex flex-col justify-between"
                    >
                        <div>
                            {/* Close Button */}
                            <div className="flex justify-between items-center mb-8">
                                <span className="text-xs font-black uppercase tracking-widest text-pink">
                                    Navigation
                                </span>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="text-gray-300 hover:text-pink transition-colors p-1.5 rounded-lg bg-white/5 border border-white/10"
                                >
                                    <HiX className="text-xl" />
                                </button>
                            </div>

                            {/* Links */}
                            <div className="flex flex-col gap-2">
                                {navigation.map((n, index) => {
                                    const isActive = router.pathname === n.path;
                                    return (
                                        <Link key={n.path} to={n.path} onClick={onClose}>
                                            <motion.div
                                                initial={{ x: 20, opacity: 0 }}
                                                animate={{ x: 0, opacity: 1 }}
                                                transition={{ duration: 0.5, delay: index * 0.06, ease: "linear" }}
                                                className={`py-2.5 px-3 rounded-xl transition-colors duration-200 ${isActive
                                                    ? "bg-pink/15 border border-pink/40 text-pink font-bold"
                                                    : "text-gray-300 hover:text-white hover:bg-white/5 font-medium"
                                                    }`}
                                            >
                                                <span className="text-sm tracking-wide">
                                                    {n.text}
                                                </span>
                                            </motion.div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Bottom Logo */}
                        <Link onClick={onClose} to="/" className="w-full text-center">
                            <div className="flex flex-col items-center gap-2 pt-6 border-t border-white/10">
                                <div className="w-12 h-12 rounded-full bg-white p-2 flex items-center justify-center">
                                    <img alt="I-FIT Indonesia Logo" src="/logo-crop.png" className="w-full h-full object-contain" />
                                </div>
                                <span className="text-xs text-gray-400 tracking-widest font-bold">
                                    I-FIT INDONESIA
                                </span>
                            </div>
                        </Link>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

export default function Header() {
    const [isOpen, setIsOpen] = useState(false);
    const router = useLocation();
    const [datas, setDatas] = useState(() => getNavigationCache() || []);
    const [scrolled, setScrolled] = useState(false);

    const navItems = (datas || [])
        .filter((item) => Boolean(item && item.path))
        .sort((a, b) => {
            const orderA = a.sort_order !== undefined && a.sort_order !== null ? Number(a.sort_order) : 999;
            const orderB = b.sort_order !== undefined && b.sort_order !== null ? Number(b.sort_order) : 999;
            if (orderA !== orderB) return orderA - orderB;
            return (Number(a.id) || 0) - (Number(b.id) || 0);
        });

    useEffect(() => {
        getNavigation().then((res) => {
            setDatas(res || []);
        });

        const handleScroll = () => {
            if (window.scrollY > 40) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <header
            className={`fixed top-0 left-0 w-full z-[100] transition-colors duration-300 ${scrolled
                ? "py-2.5 bg-[#08090d]/95 border-b border-white/10 shadow-lg"
                : "py-4 bg-transparent border-b border-transparent"
                }`}
        >
            <div className="w-[92%] max-w-[1500px] mx-auto flex items-center justify-between">
                {/* Mobile View */}
                <div className="flex xl:hidden items-center justify-between w-full">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-white p-1.5 flex items-center justify-center">
                            <img
                                alt="I-FIT Indonesia Logo"
                                src="/logo_icon.png"
                                className="w-[26px] h-[26px] object-contain"
                            />
                        </div>
                        <span className="text-lg font-black text-white tracking-wider">
                            I-FIT
                        </span>
                    </Link>

                    <button
                        type="button"
                        onClick={() => setIsOpen(true)}
                        className="p-2.5 rounded-xl bg-white/10 border border-white/15 text-white hover:bg-pink/20 hover:border-pink transition-colors duration-200"
                    >
                        <GiHamburgerMenu className="text-lg" />
                    </button>

                    <PhoneDrawer
                        navigation={navItems}
                        router={router}
                        isOpen={isOpen}
                        onClose={() => setIsOpen(false)}
                    />
                </div>

                {/* Desktop View */}
                <div className="hidden xl:flex items-center justify-between w-full">
                    {/* Brand */}
                    <Link to="/" className="flex items-center gap-3.5 group cursor-pointer">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.3, ease: "linear" }}
                            className="w-11 h-11 rounded-full bg-white p-1.5 border-2 border-pink flex items-center justify-center"
                        >
                            <img alt="I-FIT Indonesia Logo" src="/logo_icon.png" className="w-[28px] h-[28px] object-contain" />
                        </motion.div>
                        <div className="flex flex-col">
                            <span className="text-lg font-black tracking-widest text-white leading-none">
                                I-FIT
                            </span>
                            <span className="text-[9px] font-bold tracking-widest text-pink mt-0.5 leading-none">
                                SPORTS INDONESIA
                            </span>
                        </div>
                    </Link>

                    {/* Nav Items (Zero Blur, Linear Pill) */}
                    <nav className="flex items-center gap-1 bg-[#12141e]/90 px-3 py-1.5 rounded-full border border-white/10">
                        {navItems.map((data) => {
                            const isActive = router.pathname === data.path;
                            return (
                                <Link key={data.path} to={data.path}>
                                    <div className="relative px-4 py-2 rounded-full cursor-pointer transition-colors duration-200">
                                        {isActive && (
                                            <motion.div
                                                layoutId="activeNavPill"
                                                className="absolute inset-0 rounded-full bg-gradient-to-r from-pink to-ungu z-0"
                                                transition={{ duration: 0.35, ease: "linear" }}
                                            />
                                        )}
                                        <span
                                            className={`relative z-10 text-xs sm:text-sm tracking-wider font-semibold whitespace-nowrap transition-colors duration-200 ${isActive ? "text-white font-bold" : "text-gray-300 hover:text-white"
                                                }`}
                                        >
                                            {data.text}
                                        </span>
                                    </div>
                                </Link>
                            );
                        })}
                    </nav>
                </div>
            </div>
        </header>
    );
}
